import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { cn } from "@/lib/util";
import { toast } from "sonner";
import { Monitor, Loader2, LogOut, RefreshCw, Smartphone, AlertTriangle } from "lucide-react";

/**
 * Self-service list of the user's active sessions with per-session revoke +
 * "close all others" for panic mode (device stolen, credential leak, etc.).
 * Rendered inside /settings for every authenticated user.
 */
export default function SessionsSection() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [busyOthers, setBusyOthers] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/sessions");
      setSessions(r.data.sessions || []);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudieron cargar las sesiones");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (s) => {
    if (!window.confirm(
      s.current
        ? "Vas a cerrar TU sesión actual. Se te enviará al login. ¿Continuar?"
        : "Cerrar esta sesión remota. ¿Continuar?"
    )) return;
    setBusyId(s.id);
    try {
      const r = await api.post(`/auth/sessions/${s.id}/revoke`);
      if (r.data.is_current) {
        // Force logout of the current tab immediately
        window.location.href = "/login";
        return;
      }
      toast.success("Sesión cerrada");
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudo cerrar la sesión");
    } finally { setBusyId(null); }
  };

  const revokeOthers = async () => {
    if (!window.confirm(
      "Cerrar TODAS las sesiones excepto la actual. Todos tus otros dispositivos serán desconectados. ¿Continuar?"
    )) return;
    setBusyOthers(true);
    try {
      const r = await api.post("/auth/sessions/revoke-others");
      toast.success(`${r.data.revoked_count} sesión(es) cerrada(s)`);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "No se pudieron cerrar");
    } finally { setBusyOthers(false); }
  };

  return (
    <section className="border border-border bg-card/60 p-6" data-testid="settings-sessions">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 border border-primary/60 bg-primary/10 flex items-center justify-center">
          <Monitor className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1">
          <div className="overline">Cuenta</div>
          <h2 className="font-display text-2xl font-medium">Sesiones activas</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Cada login (email + contraseña + MFA) genera una sesión que muere al
            cerrar la pestaña. Si ves un dispositivo desconocido o has perdido
            uno, ciérralo aquí — el token quedará inválido al instante en el
            servidor.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={load} disabled={loading}
                  className="border border-border px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase text-muted-foreground hover:text-foreground disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="sessions-refresh-btn">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refrescar
          </button>
          {sessions.filter(s => !s.current).length > 0 && (
            <button onClick={revokeOthers} disabled={busyOthers}
                    className="border border-sev-critical/60 bg-sev-critical/10 text-sev-critical px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase hover:bg-sev-critical/20 disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="sessions-revoke-others-btn">
              {busyOthers ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
              Cerrar las demás
            </button>
          )}
        </div>
      </div>

      {loading && sessions.length === 0 ? (
        <div className="flex items-center gap-2 text-muted-foreground text-xs py-4">
          <Loader2 className="w-3 h-3 animate-spin" /> Cargando sesiones…
        </div>
      ) : sessions.length === 0 ? (
        <div className="border border-dashed border-border p-6 text-center text-muted-foreground text-xs">
          Sin sesiones activas.
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map(s => (
            <div key={s.id}
                 className={cn("border p-4 flex items-start justify-between gap-3",
                               s.current ? "border-sev-low/60 bg-sev-low/5" : "border-border bg-background/40")}
                 data-testid={`session-row-${s.id.slice(0, 8)}`}>
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className={cn("w-9 h-9 border flex items-center justify-center shrink-0",
                                   s.current ? "border-sev-low/60 bg-sev-low/10"
                                             : "border-border bg-background")}>
                  {_isMobileUA(s.user_agent)
                    ? <Smartphone className="w-4 h-4" />
                    : <Monitor className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm truncate">
                      {_prettyUA(s.user_agent)}
                    </span>
                    {s.current && (
                      <span className="font-mono text-[9px] tracking-widest uppercase border border-sev-low/60 text-sev-low bg-sev-low/10 px-2 py-0.5"
                            data-testid="session-current-badge">
                        Esta sesión
                      </span>
                    )}
                  </div>
                  <div className="mt-1 grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-0.5 text-[10px] font-mono text-muted-foreground">
                    <span>IP · {s.ip || "—"}</span>
                    <span>Inicio · {_fmt(s.created_at)}</span>
                    <span>Última actividad · {_fmt(s.last_seen_at)}</span>
                  </div>
                </div>
              </div>
              <button onClick={() => revoke(s)} disabled={busyId === s.id}
                      className={cn("shrink-0 border px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase inline-flex items-center gap-2 disabled:opacity-50",
                                    s.current
                                      ? "border-sev-critical/60 text-sev-critical hover:bg-sev-critical/10"
                                      : "border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground")}
                      data-testid={`session-revoke-btn-${s.id.slice(0, 8)}`}>
                {busyId === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <LogOut className="w-3 h-3" />}
                {s.current ? "Cerrar esta sesión" : "Cerrar"}
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function _prettyUA(ua) {
  if (!ua) return "Dispositivo desconocido";
  const s = ua.toLowerCase();
  let browser = "Navegador";
  if (s.includes("edg/")) browser = "Edge";
  else if (s.includes("chrome/") && !s.includes("chromium/")) browser = "Chrome";
  else if (s.includes("firefox/")) browser = "Firefox";
  else if (s.includes("safari/") && !s.includes("chrome/")) browser = "Safari";
  else if (s.includes("curl/")) browser = "curl";
  else if (s.includes("python-requests")) browser = "python-requests";
  let os = "";
  if (s.includes("windows")) os = "Windows";
  else if (s.includes("mac os x")) os = "macOS";
  else if (s.includes("android")) os = "Android";
  else if (s.includes("iphone") || s.includes("ipad")) os = "iOS";
  else if (s.includes("linux")) os = "Linux";
  return os ? `${browser} · ${os}` : browser;
}

function _isMobileUA(ua) {
  if (!ua) return false;
  const s = ua.toLowerCase();
  return s.includes("iphone") || s.includes("android") || s.includes("ipad");
}

function _fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      dateStyle: "short", timeStyle: "short",
    });
  } catch { return iso; }
}
